/**
 * The admin panel's route tree. Three areas, gated in `beforeLoad`:
 *   /setup/*  — first boot only (API `setup=pending`); everything else redirects here until it is done.
 *   /login    — anonymous, after setup; so are /forgot-password and /reset-password (context STORY-0168).
 *   /…        — admin, authenticated (session bootstrapped from the refresh cookie once per page load).
 *
 * There is no `/mail` here any more. The webmail is a separate application with its own bundle and its
 * own tree, and crossing to it is always a document navigation: `WEBMAIL_SIGN_IN` for a mailbox owner who
 * signed in here, or a hand-off URL the server mints for an administrator (ADR 0032).
 */
import type { QueryClient } from '@tanstack/react-query';
import {
  Outlet,
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
  type RouterHistory,
} from '@tanstack/react-router';
import { api, call, type AuditFilters } from '@mailserver/web-shared/api';
import * as session from '@mailserver/web-shared/session';
import { WEBMAIL_SIGN_IN } from '@/lib/apps';
import { NotFoundPage } from '@/routes/not-found';
import { isDomainSection } from '@/routes/domain-sections';
import { isMailboxSection } from '@/routes/mailbox-sections';
import { isServerSection } from '@/routes/server-sections';
import { isMigrationStep } from '@/routes/migration-steps';
import { isTokenTab } from '@/routes/token-tabs';

/**
 * Every screen is loaded on demand (TASK-0023). Only the three route guards below — the setup check,
 * the session bootstrap and the role redirect — are in the entry chunk, because they decide *which*
 * screen to fetch and therefore have to run before anything is fetched at all.
 *
 * `NotFoundPage` is deliberately eager: it is the one component that renders when routing has already
 * gone wrong, and a chunk fetch is the wrong thing to depend on at that moment.
 */
const AdminLayout = lazyRouteComponent(() =>
  import('@/routes/layout').then((m) => ({ default: m.AdminLayout })),
);
const AccountPage = lazyRouteComponent(() =>
  import('@/routes/account').then((m) => ({ default: m.AccountPage })),
);
const AuditPage = lazyRouteComponent(() => import('@/routes/audit').then((m) => ({ default: m.AuditPage })));
const DeliveryLogPage = lazyRouteComponent(() =>
  import('@/routes/delivery-log').then((m) => ({ default: m.DeliveryLogPage })),
);
const DashboardPage = lazyRouteComponent(() =>
  import('@/routes/dashboard').then((m) => ({ default: m.DashboardPage })),
);
const DomainPage = lazyRouteComponent(() =>
  import('@/routes/domain').then((m) => ({ default: m.DomainPage })),
);
const DomainsPage = lazyRouteComponent(() =>
  import('@/routes/domains').then((m) => ({ default: m.DomainsPage })),
);
const LicensePage = lazyRouteComponent(() =>
  import('@/routes/license').then((m) => ({ default: m.LicensePage })),
);
const MailboxPage = lazyRouteComponent(() =>
  import('@/routes/mailbox').then((m) => ({ default: m.MailboxPage })),
);
const AiPage = lazyRouteComponent(() => import('@/routes/ai').then((m) => ({ default: m.AiPage })));
const CopilotPage = lazyRouteComponent(() =>
  import('@/routes/copilot').then((m) => ({ default: m.CopilotPage })),
);
const MigrationPage = lazyRouteComponent(() =>
  import('@/routes/migration').then((m) => ({ default: m.MigrationPage })),
);
const MigrationRedirect = lazyRouteComponent(() =>
  import('@/routes/migration').then((m) => ({ default: m.MigrationRedirect })),
);
const NewMigrationPage = lazyRouteComponent(() =>
  import('@/routes/migrations').then((m) => ({ default: m.NewMigrationPage })),
);
const MigrationsPage = lazyRouteComponent(() =>
  import('@/routes/migrations').then((m) => ({ default: m.MigrationsPage })),
);
const QueuePage = lazyRouteComponent(() => import('@/routes/queue').then((m) => ({ default: m.QueuePage })));
const ServerPage = lazyRouteComponent(() =>
  import('@/routes/server').then((m) => ({ default: m.ServerPage })),
);
const TokensPage = lazyRouteComponent(() =>
  import('@/routes/tokens').then((m) => ({ default: m.TokensPage })),
);
const AuthBansPage = lazyRouteComponent(() =>
  import('@/routes/auth-bans').then((m) => ({ default: m.AuthBansPage })),
);
const UsersPage = lazyRouteComponent(() => import('@/routes/users').then((m) => ({ default: m.UsersPage })));
const SetupWizard = lazyRouteComponent(() =>
  import('@/routes/setup/wizard').then((m) => ({ default: m.SetupWizard })),
);
const ForgotPasswordPage = lazyRouteComponent(() =>
  import('@/routes/password-reset').then((m) => ({ default: m.ForgotPasswordPage })),
);
const ResetPasswordPage = lazyRouteComponent(() =>
  import('@/routes/password-reset').then((m) => ({ default: m.ResetPasswordPage })),
);
const LoginPage = lazyRouteComponent(() => import('@/routes/login').then((m) => ({ default: m.LoginPage })));

interface RouterContext {
  queryClient: QueryClient;
}

/** Setup state is checked once per page load; the wizard's final step flips it locally on completion. */
let setupState: 'pending' | 'complete' | null = null;
export async function getSetupState(): Promise<'pending' | 'complete'> {
  if (setupState) return setupState;
  const h = await call(() => api.GET('/health'));
  setupState = h.setup;
  return setupState;
}
export const markSetupComplete = () => {
  setupState = 'complete';
};
/** Test hook: forget the cached setup state and session between renders. */
export const resetAppState = () => {
  setupState = null;
  session.reset();
};

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Outlet,
  notFoundComponent: NotFoundPage,
});

const setupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/setup',
  beforeLoad: async () => {
    if ((await getSetupState()) === 'complete') throw redirect({ to: '/login' });
  },
  component: SetupWizard,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: (s: Record<string, unknown>): { redirect?: string } =>
    typeof s['redirect'] === 'string' ? { redirect: s['redirect'] } : {},
  beforeLoad: async ({ search }) => {
    if ((await getSetupState()) === 'pending') throw redirect({ to: '/setup' });
    const st = await session.init();
    if (st.status !== 'authenticated') return;
    // An already-signed-in mailbox owner belongs in the other application, not on this login screen.
    if (st.user.role === 'mailbox_user') {
      window.location.assign(WEBMAIL_SIGN_IN);
      return;
    }
    throw redirect({ to: search.redirect ?? '/' });
  },
  component: LoginPage,
});

/** Password reset by email (context STORY-0168): anonymous, after setup, like `/login`. */
const anonymousAfterSetup = async () => {
  if ((await getSetupState()) === 'pending') throw redirect({ to: '/setup' });
};
const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  beforeLoad: anonymousAfterSetup,
  component: ForgotPasswordPage,
});
const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  validateSearch: (s: Record<string, unknown>): { token?: string } =>
    typeof s['token'] === 'string' ? { token: s['token'] } : {},
  beforeLoad: anonymousAfterSetup,
  component: ResetPasswordPage,
});

const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'admin',
  beforeLoad: async ({ location }) => {
    if ((await getSetupState()) === 'pending') throw redirect({ to: '/setup' });
    const st = await session.init();
    if (st.status !== 'authenticated') throw redirect({ to: '/login', search: { redirect: location.href } });
    // mailbox owners have no admin screens: their home is the webmail, which is a different app
    if (st.user.role === 'mailbox_user') window.location.assign(WEBMAIL_SIGN_IN);
  },
  component: AdminLayout,
});

// Literal paths must stay inline (a helper would widen them to `string` and break typed links).
const admin = { getParentRoute: () => adminRoute };
const routeTree = rootRoute.addChildren([
  setupRoute,
  loginRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  adminRoute.addChildren([
    createRoute({ ...admin, path: '/', component: DashboardPage }),
    createRoute({ ...admin, path: '/domains', component: DomainsPage }),
    // A domain's page is its sections (context STORY-0266); the bare path is its first one.
    createRoute({
      ...admin,
      path: '/domains/$domainId',
      beforeLoad: ({ params }) => {
        throw redirect({
          to: '/domains/$domainId/$section',
          params: { ...params, section: 'mailboxes' },
          replace: true,
        });
      },
    }),
    createRoute({
      ...admin,
      path: '/domains/$domainId/$section',
      beforeLoad: ({ params }) => {
        if (!isDomainSection(params.section))
          throw redirect({
            to: '/domains/$domainId/$section',
            params: { ...params, section: 'mailboxes' },
            replace: true,
          });
      },
      component: DomainPage,
    }),
    // A mailbox's page is its sections too (context STORY-0267); the bare path is Account.
    createRoute({
      ...admin,
      path: '/mailboxes/$mailboxId',
      beforeLoad: ({ params }) => {
        throw redirect({
          to: '/mailboxes/$mailboxId/$section',
          params: { ...params, section: 'account' },
          replace: true,
        });
      },
    }),
    createRoute({
      ...admin,
      path: '/mailboxes/$mailboxId/$section',
      beforeLoad: ({ params }) => {
        if (!isMailboxSection(params.section))
          throw redirect({
            to: '/mailboxes/$mailboxId/$section',
            params: { ...params, section: 'account' },
            replace: true,
          });
      },
      component: MailboxPage,
    }),
    createRoute({ ...admin, path: '/users', component: UsersPage }),
    // Two lists, two URLs (context STORY-0271): server automation and applications.
    createRoute({
      ...admin,
      path: '/tokens',
      beforeLoad: () => {
        throw redirect({ to: '/tokens/$tab', params: { tab: 'server' }, replace: true });
      },
    }),
    createRoute({
      ...admin,
      path: '/tokens/$tab',
      beforeLoad: ({ params }) => {
        if (!isTokenTab(params.tab))
          throw redirect({ to: '/tokens/$tab', params: { tab: 'server' }, replace: true });
      },
      component: TokensPage,
    }),
    createRoute({ ...admin, path: '/blocked-sign-ins', component: AuthBansPage }),
    // Settings are sections too (context STORY-0268); the bare path is General.
    createRoute({
      ...admin,
      path: '/server',
      beforeLoad: () => {
        throw redirect({ to: '/server/$section', params: { section: 'general' }, replace: true });
      },
    }),
    createRoute({
      ...admin,
      path: '/server/$section',
      beforeLoad: ({ params }) => {
        if (!isServerSection(params.section))
          throw redirect({ to: '/server/$section', params: { section: 'general' }, replace: true });
      },
      component: ServerPage,
    }),
    createRoute({ ...admin, path: '/queue', component: QueuePage }),
    createRoute({
      ...admin,
      path: '/audit',
      component: AuditPage,
      // The filters are in the URL, so a filtered log can be linked to (context STORY-0273).
      validateSearch: (s: Record<string, unknown>): AuditFilters => {
        // A value the page cannot use is dropped, not passed on: a hand-edited `from=2026-10` would
        // otherwise be an invalid date the page throws on while rendering (context BUG-0278). Dropped
        // as `undefined`, not left out: the router merges the raw search under what this returns.
        const ok = (k: string, test: (v: string) => boolean = (v) => v !== '') => {
          const v = s[k];
          return typeof v === 'string' && test(v) ? v : undefined;
        };
        const day = (v: string) =>
          /^\d{4}-\d{2}-\d{2}$/.test(v) && !Number.isNaN(Date.parse(`${v}T00:00:00Z`));
        const uuid = (v: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);
        const out = {
          actor: ok('actor', uuid),
          action: ok('action'),
          entity: ok('entity'),
          from: ok('from', day),
          to: ok('to', day),
        };
        return out as AuditFilters;
      },
    }),
    createRoute({ ...admin, path: '/license', component: LicensePage }),
    createRoute({
      ...admin,
      path: '/delivery-log',
      component: DeliveryLogPage,
      // `class` is here so an alert can link straight to the rows behind it — the rate-limit burst
      // alert lands on that sender's `throttled` events rather than on the unfiltered log.
      validateSearch: (s: Record<string, unknown>): { q?: string; class?: string } => ({
        ...(typeof s['q'] === 'string' ? { q: s['q'] } : {}),
        ...(typeof s['class'] === 'string' ? { class: s['class'] } : {}),
      }),
    }),
    createRoute({ ...admin, path: '/ai', component: AiPage }),
    createRoute({ ...admin, path: '/copilot', component: CopilotPage }),
    createRoute({ ...admin, path: '/migrations', component: MigrationsPage }),
    // A migration is a stepper (context STORY-0270): creating one is its first step, at /migrations/new,
    // and a job's bare URL opens it at the step it has reached.
    createRoute({ ...admin, path: '/migrations/new', component: NewMigrationPage }),
    createRoute({ ...admin, path: '/migrations/$migrationId', component: MigrationRedirect }),
    createRoute({
      ...admin,
      path: '/migrations/$migrationId/$step',
      beforeLoad: ({ params }) => {
        if (!isMigrationStep(params.step))
          throw redirect({
            to: '/migrations/$migrationId',
            params: { migrationId: params.migrationId },
            replace: true,
          });
      },
      component: MigrationPage,
    }),
    createRoute({ ...admin, path: '/account', component: AccountPage }),
  ]),
]);

export function createAppRouter(queryClient: QueryClient, history?: RouterHistory) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
    ...(history ? { history } : {}),
  });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
